/**
 * /field/pf — a Pipeline & Facility hand's own jobs (GET
 * /pipeline-facility/field/my-jobs, fieldiq-api#1200, PF-2).
 *
 * THE SELECTION IS THE API'S: the jobs whose crew the caller is on that are
 * scheduled or in progress. This screen only groups them, in the API's order.
 * The field surface is SNAKE_CASE (job_number, today_log_id).
 *
 * The frame is Pressure Testing's field frame (PtFieldPage), imported, so the
 * verticals' field screens stay one shape.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronRight, Layers, MapPin } from 'lucide-react';
import { usePfFieldJobs } from '../../../hooks/usePipelineFacility';
import { pfDay, pfJobStatusSkin } from '../../../lib/pipeline-facility';
import type { PfFieldJob } from '../../../types/pipeline-facility';
import { PT_FIELD_SECONDARY, PtFieldBadge, PtFieldCard, PtFieldEmpty, PtFieldPage, PtFieldQueryState, PtFieldSectionTitle } from '../../pressure-testing/field/PtFieldBits';
import { chainageRange } from '../JobsScreen';

function JobCard({ job, chainageUnit }: { job: PfFieldJob; chainageUnit: string | null }) {
  return (
    <Link to={`/field/pf/jobs/${job.id}`} className="block" data-testid="pf-my-job">
      <PtFieldCard className="flex min-h-[88px] items-center gap-3 px-4 py-4 active:bg-fieldiq-dark/50">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-base font-extrabold text-fieldiq-text">{job.job_number || '—'}</span>
            <PtFieldBadge skin={pfJobStatusSkin(job.status)} />
          </div>
          <p className="mt-1 truncate text-base font-bold text-white">{job.customer_name || '—'}</p>
          <p className="mt-0.5 flex items-center gap-1 truncate text-sm text-fieldiq-text">
            <MapPin className="size-3.5 shrink-0 text-fieldiq-muted" aria-hidden="true" />
            {job.site_name || '—'}
            {job.from_chainage != null || job.to_chainage != null ? ` · ${chainageRange(job.from_chainage, job.to_chainage, chainageUnit)}` : ''}
          </p>
          <p className="mt-1 flex items-center gap-1 text-xs text-fieldiq-muted">
            {job.today_log_id ? (
              <>
                <CheckCircle2 className="size-3.5 text-fi-green" aria-hidden="true" />
                Today&apos;s log written
              </>
            ) : job.status === 'scheduled' ? (
              `Starts ${pfDay(job.scheduled_start)}`
            ) : (
              'No log yet today'
            )}
          </p>
        </div>
        <ChevronRight className="size-6 shrink-0 text-fieldiq-muted" aria-hidden="true" />
      </PtFieldCard>
    </Link>
  );
}

export default function PfMyJobsScreen() {
  const query = usePfFieldJobs();
  const jobs = query.data?.data ?? [];
  const chainageUnit = query.data?.meta.units?.chainage ?? null;
  const groups = [
    { key: 'in_progress', title: 'In progress', jobs: jobs.filter((j) => j.status === 'in_progress') },
    { key: 'scheduled', title: 'Scheduled', jobs: jobs.filter((j) => j.status === 'scheduled') },
  ].filter((g) => g.jobs.length > 0);

  return (
    <PtFieldPage title="My PF Jobs" backTo="/field" testId="pf-my-jobs">
      <Link to="/field/pf/digs" className={`${PT_FIELD_SECONDARY} mb-4`} data-testid="pf-my-digs-link">
        <Layers className="size-5" aria-hidden="true" />
        My digs
      </Link>
      <PtFieldQueryState query={query} subject="your pipeline & facility jobs">
        {groups.length === 0 ? (
          <PtFieldEmpty>You are not on the crew of any open pipeline or facility job. The office puts you on a job&apos;s crew.</PtFieldEmpty>
        ) : (
          groups.map((g) => (
            <section key={g.key} aria-labelledby={`pf-group-${g.key}`}>
              <PtFieldSectionTitle>
                <span id={`pf-group-${g.key}`}>
                  {g.title} · {g.jobs.length}
                </span>
              </PtFieldSectionTitle>
              <div className="space-y-2">
                {g.jobs.map((j) => (
                  <JobCard key={j.id} job={j} chainageUnit={chainageUnit} />
                ))}
              </div>
            </section>
          ))
        )}
      </PtFieldQueryState>
    </PtFieldPage>
  );
}
